'use client';

import { useTransition } from 'react';
import { useRouter } from 'next/navigation';
import { ButtonVariants } from '@pixum/button';
import { useCartStore } from 'stores/cart';
import { logger } from 'utils';
import { clearCartAddressesAction } from '../../actions/clearCartAddressesAction';
import { validatePayPalExpressAddressesAction } from '../../actions/validatePayPalExpressAddressesAction';
import { inactiveArticlesAction } from '../../actions/inactiveArticlesAction';
import { trackProceedButtonClick } from '../../analytics/overview';
import type { CartRoutes, ExpressCheckoutInfo } from '../../types';
import type { CartArticleType } from 'services/types';
import { ProceedButton } from './ProceedButton';

interface ExpressCheckoutProceedButtonProps {
  expressCheckoutInfo: ExpressCheckoutInfo;
  userId: string;
  routes: CartRoutes;
  constants: Record<string, string>;
  totalArticleCount: number;
  inactiveArticles: Pick<CartArticleType, 'id' | 'positionId' | 'articleGroup' | 'type'>[];
  inactiveChildIds: number[];
  isAuthenticated: boolean;
}

export function ExpressCheckoutProceedButton(props: ExpressCheckoutProceedButtonProps) {
  const {
    expressCheckoutInfo,
    userId,
    routes,
    constants,
    totalArticleCount,
    inactiveArticles,
    inactiveChildIds,
    isAuthenticated
  } = props;

  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const { isBusy, setCartBusy } = useCartStore();
  const canClick = !isPending && !isBusy;

  const handleClick = (e: React.SyntheticEvent) => {
    e.preventDefault();
    trackProceedButtonClick();

    startTransition(async () => {
      try {
        setCartBusy(true);

        if (inactiveArticles.length > 0 || inactiveChildIds.length > 0) {
          const deleteResult = await inactiveArticlesAction({ inactiveArticles, inactiveChildIds, userId });

          if (!deleteResult.success) {
            setCartBusy(false);

            return;
          }

          const remainingArticleCount = totalArticleCount - inactiveArticles.length;

          if (remainingArticleCount === 0) {
            setCartBusy(false);

            return;
          }
        }

        if (!isAuthenticated) {
          router.push(routes.login);
          setCartBusy(false);

          return;
        }

        const info = expressCheckoutInfo;

        if (info.shouldClearAddress) {
          const clearResult = await clearCartAddressesAction({ userId });

          if (!clearResult.success) {
            setCartBusy(false);

            return;
          }
        }

        if (info.shouldValidatePayPalAddress) {
          const validationResult = await validatePayPalExpressAddressesAction();

          if (!validationResult.success) {
            logger.error({
              ns: 'cartExpressCheckout',
              type: 'PAYPAL_VALIDATION_ERROR',
              severity: 'high',
              msg: typeof validationResult.error === 'string' ? validationResult.error : 'PayPal validation failed'
            });
            setCartBusy(false);
            router.push(routes.address);

            return;
          }

          router.push(info.url);
          setCartBusy(false);

          return;
        }

        router.push(info.url);
        logger.info({
          ns: 'cartExpressCheckout',
          msg: 'Express checkout navigation',
          reason: info.reason,
          metadata: info.metadata
        });
        setCartBusy(false);
      } catch (err) {
        logger.error({
          ns: 'cartExpressCheckout',
          type: 'EXPRESS_CHECKOUT_ERROR',
          severity: 'high',
          msg: (err as Error).message
        });
        setCartBusy(false);
      }
    });
  };

  return (
    <ProceedButton
      href={isAuthenticated ? expressCheckoutInfo.url : routes.login}
      isBusy={isPending || isBusy}
      isClickable={canClick}
      onClick={handleClick}
      variant={ButtonVariants.STRONG}
    >
      {constants['i18n_proceed-to-checkout']}
    </ProceedButton>
  );
}
